import { useState } from 'react';
import { FiTrendingUp, FiClock, FiCheckCircle } from 'react-icons/fi';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import toast from 'react-hot-toast';
import { isQaTesterRole } from '../utils/roleUtils';
import { departmentUsesBugTracker, getDepartmentKpiHints } from '../constants/departments';
import { buildCompletedWorkSummary } from '../utils/memberDashboardInsights';
import { useMemberKpis, useMemberKpiMutations } from '../api/memberKpiApi';

const cardStyle = {
  background: '#fff',
  padding: '1rem 1.15rem',
  borderRadius: 12,
  border: '1px solid #e2e8f0',
  boxShadow: '0 1px 4px rgba(0,0,0,0.04)',
};

const kpiLabel = { fontSize: '0.78rem', fontWeight: 600, color: '#64748b', marginBottom: 4 };
const kpiValue = { fontSize: '1.75rem', fontWeight: 800, color: '#1e293b', lineHeight: 1 };
const kpiSub = { fontSize: '0.72rem', color: '#94a3b8', marginTop: 4 };

const inputStyle = {
  width: '100%',
  padding: '0.5rem 0.65rem',
  border: '1px solid #e2e8f0',
  borderRadius: 8,
  fontSize: '0.82rem',
  boxSizing: 'border-box',
  background: '#f8fafc',
};

function KpiProgressBar({ current, target }) {
  const pct = target > 0 ? Math.min(100, Math.round((current / target) * 100)) : 0;
  const color = pct >= 100 ? '#10b981' : pct >= 70 ? '#2C8FC7' : '#f59e0b';
  return (
    <div style={{ marginTop: 8 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.72rem', color: '#64748b', marginBottom: 4 }}>
        <span>{current} / {target}</span>
        <span style={{ fontWeight: 700, color }}>{pct}%</span>
      </div>
      <div style={{ height: 6, background: '#f1f5f9', borderRadius: 999, overflow: 'hidden' }}>
        <div style={{ width: `${pct}%`, height: '100%', background: color, borderRadius: 999 }} />
      </div>
    </div>
  );
}

function MemberKpiPanel({ memberUserId, canManage, memberDepartment }) {
  const { data, isLoading } = useMemberKpis(memberUserId);
  const { create, update, remove } = useMemberKpiMutations(memberUserId);
  const kpis = data?.kpis || [];
  const hints = getDepartmentKpiHints(memberDepartment);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({
    title: '',
    target_value: '100',
    current_value: '0',
    unit: '',
    period_label: 'Ongoing',
    notes: '',
  });

  const resetForm = () => {
    setForm({ title: '', target_value: '100', current_value: '0', unit: '', period_label: 'Ongoing', notes: '' });
    setShowForm(false);
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) return;
    try {
      await create.mutateAsync({
        title: form.title.trim(),
        target_value: Number(form.target_value) || 100,
        current_value: Number(form.current_value) || 0,
        unit: form.unit.trim(),
        period_label: form.period_label.trim() || 'Ongoing',
        notes: form.notes.trim() || undefined,
      });
      toast.success('KPI added');
      resetForm();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to add KPI');
    }
  };

  const handleProgressUpdate = async (kpi, nextCurrent) => {
    try {
      await update.mutateAsync({
        kpiId: kpi.id,
        payload: { current_value: nextCurrent },
      });
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to update KPI');
    }
  };

  return (
    <div style={{ marginTop: '1.25rem' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem', gap: 8, flexWrap: 'wrap' }}>
        <div>
          <h4 style={{ margin: 0, fontSize: '0.95rem', fontWeight: 700, color: '#1e293b' }}>
            Manager KPIs
          </h4>
          <p style={{ margin: '4px 0 0', fontSize: '0.72rem', color: '#64748b' }}>
            {hints.panelSubtitle}
          </p>
        </div>
        {canManage && (
          <button
            type="button"
            onClick={() => setShowForm((v) => !v)}
            style={{
              padding: '0.45rem 0.85rem',
              borderRadius: 8,
              border: 'none',
              background: '#1272B3',
              color: '#fff',
              fontWeight: 700,
              fontSize: '0.78rem',
              cursor: 'pointer',
            }}
          >
            {showForm ? 'Cancel' : '+ Add KPI'}
          </button>
        )}
      </div>

      {showForm && canManage && (
        <form onSubmit={handleCreate} style={{ ...cardStyle, marginBottom: '0.75rem', display: 'grid', gap: '0.65rem' }}>
          <input style={inputStyle} placeholder={hints.titlePlaceholder} value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required />
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0.5rem' }}>
            <input style={inputStyle} type="number" min={1} placeholder="Target" value={form.target_value} onChange={(e) => setForm({ ...form, target_value: e.target.value })} />
            <input style={inputStyle} type="number" min={0} placeholder="Current" value={form.current_value} onChange={(e) => setForm({ ...form, current_value: e.target.value })} />
            <input style={inputStyle} placeholder={hints.unitPlaceholder} value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })} />
          </div>
          <input style={inputStyle} placeholder={hints.periodPlaceholder} value={form.period_label} onChange={(e) => setForm({ ...form, period_label: e.target.value })} />
          <textarea style={{ ...inputStyle, minHeight: 56, resize: 'vertical' }} placeholder="Notes (optional)" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
          <button type="submit" disabled={create.isPending} style={{ padding: '0.55rem', borderRadius: 8, border: 'none', background: '#16a34a', color: '#fff', fontWeight: 700, cursor: 'pointer' }}>
            {create.isPending ? 'Saving…' : 'Save KPI'}
          </button>
        </form>
      )}

      {isLoading ? (
        <div style={{ textAlign: 'center', padding: '1rem', color: '#64748b' }}>Loading KPIs…</div>
      ) : kpis.length === 0 ? (
        <div style={{ ...cardStyle, color: '#94a3b8', fontSize: '0.85rem', textAlign: 'center' }}>
          {canManage ? 'No KPIs yet — add targets to track this member progress.' : 'No manager KPIs defined yet.'}
        </div>
      ) : (
        <div style={{ display: 'grid', gap: '0.65rem' }}>
          {kpis.map((kpi) => (
            <div key={kpi.id} style={cardStyle}>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'flex-start' }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 700, color: '#1e293b', fontSize: '0.88rem' }}>{kpi.title}</div>
                  <div style={{ fontSize: '0.72rem', color: '#64748b', marginTop: 2 }}>{kpi.period_label}{kpi.unit ? ` · ${kpi.unit}` : ''}</div>
                </div>
                {canManage && (
                  <button
                    type="button"
                    onClick={() => remove.mutate(kpi.id, { onSuccess: () => toast.success('KPI removed') })}
                    style={{ background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer', fontSize: '1rem' }}
                    title="Delete KPI"
                  >
                    ×
                  </button>
                )}
              </div>
              <KpiProgressBar current={kpi.current_value} target={kpi.target_value} />
              {canManage && (
                <div style={{ display: 'flex', gap: 8, marginTop: 10, alignItems: 'center' }}>
                  <span style={{ fontSize: '0.72rem', color: '#64748b' }}>Update progress:</span>
                  <input
                    type="number"
                    min={0}
                    defaultValue={kpi.current_value}
                    key={`${kpi.id}-${kpi.current_value}`}
                    style={{ ...inputStyle, width: 88, padding: '0.35rem 0.5rem' }}
                    onBlur={(e) => {
                      const val = Number(e.target.value);
                      if (Number.isFinite(val) && val !== kpi.current_value) handleProgressUpdate(kpi, val);
                    }}
                  />
                </div>
              )}
              {kpi.notes && (
                <div style={{ marginTop: 8, fontSize: '0.75rem', color: '#475569', lineHeight: 1.4 }}>{kpi.notes}</div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * All-time / career performance summary for manager profile modal (Performance tab).
 */
const MemberPerformanceOverview = ({
  stats = {},
  pastWork = [],
  sprintTimeline = [],
  projects = [],
  memberRole,
  memberDepartment,
  memberUserId,
  canManageKpis,
}) => {
  const totalAssigned = Number(stats.total_assigned || 0);
  const totalClosed = Number(stats.total_closed || 0);
  const openCount = Number(stats.open_count || 0);
  const inProgressCount = Number(stats.in_progress_count || 0);
  const blockersOpen = Number(stats.blockers_open || 0);
  const storyPointsDone = Number(stats.story_points_done || 0);
  const tasksCount = Number(stats.tasks_count || 0);
  const bugsCount = Number(stats.bugs_count || 0);
  const closedWeek = Number(stats.closed_this_week || 0);
  const closedMonth = Number(stats.closed_this_month || 0);
  const avgDays = stats.avg_days_to_close;
  const completionRate = totalAssigned > 0 ? Math.round((totalClosed / totalAssigned) * 100) : 0;
  const showBugKpi = departmentUsesBugTracker(memberDepartment)
    && (isQaTesterRole(memberRole) || bugsCount > 0);

  const { bullets: summaryBullets, highlights: recentHighlights } = buildCompletedWorkSummary({
    stats,
    pastWork,
    sprintTimeline,
    showBugs: showBugKpi,
  });

  const workMix = [
    { label: 'Completed', value: totalClosed, color: '#10b981' },
    { label: 'In Progress', value: inProgressCount, color: '#2C8FC7' },
    { label: 'Open', value: openCount, color: '#f59e0b' },
  ];

  const recentSprints = (sprintTimeline || []).slice(0, 6);

  return (
    <div style={{ marginBottom: '1rem' }}>
      <div style={{
        background: 'linear-gradient(135deg, #0f766e 0%, #14b8a6 100%)',
        borderRadius: 14,
        padding: '1.15rem 1.35rem',
        color: '#fff',
        marginBottom: '1rem',
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: '1rem' }}>
          <div>
            <div style={{ fontSize: '0.78rem', opacity: 0.9, marginBottom: 4 }}>Overall performance</div>
            <div style={{ fontSize: '1.35rem', fontWeight: 800 }}>{totalClosed} items completed</div>
            <div style={{ fontSize: '0.82rem', opacity: 0.9, marginTop: 4 }}>
              {completionRate}% completion across all assigned work
            </div>
          </div>
          <div style={{ display: 'flex', gap: '1.25rem', flexWrap: 'wrap' }}>
            <div>
              <div style={{ fontSize: '0.72rem', opacity: 0.85 }}>This week</div>
              <div style={{ fontSize: '1.4rem', fontWeight: 800 }}>{closedWeek}</div>
            </div>
            <div>
              <div style={{ fontSize: '0.72rem', opacity: 0.85 }}>This month</div>
              <div style={{ fontSize: '1.4rem', fontWeight: 800 }}>{closedMonth}</div>
            </div>
            <div>
              <div style={{ fontSize: '0.72rem', opacity: 0.85 }}>Avg close</div>
              <div style={{ fontSize: '1.4rem', fontWeight: 800 }}>{avgDays ?? '—'}{avgDays != null ? 'd' : ''}</div>
            </div>
          </div>
        </div>
      </div>

      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
        gap: '0.65rem',
        marginBottom: '1rem',
      }}>
        <div style={cardStyle}>
          <div style={kpiLabel}>Total assigned</div>
          <div style={kpiValue}>{totalAssigned}</div>
          <div style={kpiSub}>All time</div>
        </div>
        <div style={cardStyle}>
          <div style={kpiLabel}>Completed</div>
          <div style={{ ...kpiValue, color: '#16a34a' }}>{totalClosed}</div>
          <div style={kpiSub}>{completionRate}% rate</div>
        </div>
        <div style={cardStyle}>
          <div style={kpiLabel}>Open</div>
          <div style={kpiValue}>{openCount}</div>
          <div style={kpiSub}>{inProgressCount} in progress</div>
        </div>
        <div style={cardStyle}>
          <div style={kpiLabel}>Blockers</div>
          <div style={{ ...kpiValue, color: blockersOpen > 0 ? '#dc2626' : '#10b981' }}>{blockersOpen}</div>
          <div style={kpiSub}>Open priority items</div>
        </div>
        <div style={cardStyle}>
          <div style={kpiLabel}>Story pts done</div>
          <div style={kpiValue}>{storyPointsDone}</div>
          <div style={kpiSub}>Closed work</div>
        </div>
        <div style={cardStyle}>
          <div style={kpiLabel}>Tasks</div>
          <div style={kpiValue}>{tasksCount}</div>
          <div style={kpiSub}>Assigned total</div>
        </div>
        {showBugKpi && (
          <div style={cardStyle}>
            <div style={kpiLabel}>Bugs</div>
            <div style={kpiValue}>{bugsCount}</div>
            <div style={kpiSub}>Assigned total</div>
          </div>
        )}
        <div style={cardStyle}>
          <div style={kpiLabel}>Projects</div>
          <div style={kpiValue}>{projects.length}</div>
          <div style={kpiSub}>Assigned</div>
        </div>
      </div>

      <div style={{ ...cardStyle, marginBottom: '1rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
          <FiCheckCircle size={16} color="#0f766e" />
          <h4 style={{ margin: 0, fontSize: '0.92rem', fontWeight: 700, color: '#1e293b' }}>
            Summary
          </h4>
        </div>
        <ul style={{ margin: 0, paddingLeft: '1.15rem', display: 'grid', gap: 6 }}>
          {summaryBullets.map((line) => (
            <li key={line} style={{ fontSize: '0.82rem', color: '#475569', lineHeight: 1.5 }}>
              {line}
            </li>
          ))}
        </ul>
        {recentHighlights.length > 0 && (
          <div style={{ marginTop: '0.85rem', paddingTop: '0.85rem', borderTop: '1px solid #e2e8f0' }}>
            <div style={{ fontSize: '0.72rem', fontWeight: 700, color: '#64748b', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
              Recent tasks &amp; stories
            </div>
            <ul style={{ margin: 0, padding: 0, listStyle: 'none', display: 'grid', gap: 6 }}>
              {recentHighlights.map((item, idx) => (
                <li
                  key={`${item.type}-${item.title}-${idx}`}
                  style={{
                    fontSize: '0.8rem',
                    color: '#334155',
                    padding: '0.45rem 0.65rem',
                    background: '#f8fafc',
                    borderRadius: 8,
                    border: '1px solid #e2e8f0',
                  }}
                >
                  <span style={{
                    fontSize: '0.65rem',
                    fontWeight: 700,
                    color: '#0f766e',
                    background: '#ecfdf5',
                    padding: '2px 6px',
                    borderRadius: 4,
                    marginRight: 6,
                  }}>
                    {String(item.type).toUpperCase()}
                  </span>
                  {item.title}
                  <span style={{ color: '#94a3b8', fontSize: '0.72rem' }}>
                    {' '}· {item.sprint}{item.points != null && item.points > 0 ? ` · ${item.points} pts` : ''}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
        gap: '0.85rem',
        marginBottom: '1rem',
      }}>
        <div style={cardStyle}>
          <h4 style={{ margin: '0 0 4px', fontSize: '0.92rem', fontWeight: 700, color: '#1e293b' }}>
            Workload snapshot
          </h4>
          <div style={{ fontSize: '0.72rem', color: '#64748b', marginBottom: 8 }}>
            All assigned work by status
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6, fontSize: '0.75rem', color: '#64748b' }}>
            <FiClock size={14} />
            Completion rate: {completionRate}%
          </div>
          <ResponsiveContainer width="100%" height={180}>
            <BarChart data={workMix} margin={{ top: 8, right: 8, left: 0, bottom: 4 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis dataKey="label" tick={{ fontSize: 11 }} />
              <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
              <Tooltip />
              <Bar dataKey="value" radius={[4, 4, 0, 0]}>
                {workMix.map((entry) => <Cell key={entry.label} fill={entry.color} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div style={cardStyle}>
          <h4 style={{ margin: '0 0 4px', fontSize: '0.92rem', fontWeight: 700, color: '#1e293b' }}>
            Sprint history
          </h4>
          <div style={{ fontSize: '0.72rem', color: '#64748b', marginBottom: 8 }}>
            Recent sprints this member contributed to
          </div>
          {recentSprints.length === 0 ? (
            <div style={{ color: '#94a3b8', fontSize: '0.82rem', padding: '1rem 0' }}>No sprint history yet</div>
          ) : (
            <div style={{ display: 'grid', gap: '0.45rem', maxHeight: 200, overflowY: 'auto' }}>
              {recentSprints.map((s) => {
                const total = Number(s.total || 0);
                const done = Number(s.done || 0);
                const pct = total > 0 ? Math.round((done / total) * 100) : 0;
                return (
                  <div key={s.id} style={{ padding: '0.55rem 0.65rem', background: '#f8fafc', borderRadius: 8, border: '1px solid #e2e8f0' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                      <div style={{ fontSize: '0.8rem', fontWeight: 600, color: '#1e293b' }}>{s.sprint_name}</div>
                      <span style={{ fontSize: '0.68rem', fontWeight: 700, color: s.status === 'Active' ? '#16a34a' : '#64748b' }}>{s.status}</span>
                    </div>
                    <div style={{ fontSize: '0.72rem', color: '#64748b', marginTop: 2 }}>{s.projectname} · {done}/{total} done ({pct}%)</div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      <div style={{ ...cardStyle, background: '#f8fafc' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
          <FiTrendingUp size={16} color="#0f766e" />
          <span style={{ fontWeight: 700, color: '#0f766e', fontSize: '0.85rem' }}>Manager summary</span>
        </div>
        <p style={{ margin: 0, fontSize: '0.82rem', color: '#475569', lineHeight: 1.5 }}>
          {totalClosed > 0
            ? `Closed ${totalClosed} items overall with ${completionRate}% completion. `
            : 'No completed items recorded yet. '}
          {closedWeek > 0 ? `${closedWeek} closed this week. ` : ''}
          {blockersOpen > 0
            ? `${blockersOpen} open blocker/critical item${blockersOpen !== 1 ? 's' : ''} need attention.`
            : 'No open blocker-priority items on their queue.'}
        </p>
      </div>

      {memberUserId && (
        <MemberKpiPanel
          memberUserId={memberUserId}
          canManage={canManageKpis}
          memberDepartment={memberDepartment}
        />
      )}

      <div style={{ borderTop: '1px solid #e2e8f0', marginTop: '1.25rem' }} />
    </div>
  );
};

export default MemberPerformanceOverview;
